/* SearchPage — iter55 rebuild (Feb 2026)
 *
 * College-football-first discovery experience. Compact header +
 * search input, rotating placeholders, Recent Searches, Explore
 * College Football chips, Trending, Pulse-powered "What's Happening
 * Now" and "Recently Active", Fresh CFB Stories, Discover Athletes,
 * Programs, secondary MLB, and Coming Soon.
 *
 * Backend fully reused: /api/search, /api/athletes, /api/stories,
 * /api/schools/public, /api/pulse/live, /api/collections.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search as SearchIcon, TrendingUp, Flame, ArrowRight, X, Users,
  Bookmark, BadgeCheck, ChevronRight, Sparkles, Activity, Bell, Play, BookOpen,
} from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { toast } from 'sonner';
import { resolveImageUrl } from '../components/ImageUpload';
import { linkHoverProps } from '../lib/nativeCache';
import AthleteAvatarFallback, { SCHOOL_PRIMARY_COLOR } from '../components/athlete/AthleteAvatarFallback';
import { timeAgo, eventHref } from '../components/pulse/ThePulseStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENT_KEY = 'flyt_search_recent_v1';

/* ═══════════════════════════════════════════════════════════════════════
 * Constants
 * ═════════════════════════════════════════════════════════════════════ */

const PLACEHOLDERS = [
  'Search athletes, schools, teams, stories…',
  'Search Texas A&M',
  'Search TCU Football',
  'Search Nazir Stackhouse',
  'Search NFL Draft',
  'Search recruiting stories',
];

/* iter587 · SearchAvatar — every athlete row in search gets an
 * avatar, no exceptions. When a photo is set we render it; when the
 * photo fails to load OR was never set, we fall back to the shared
 * `AthleteAvatarFallback` (school-tinted disk + initials/sport glyph)
 * so the search list never shows empty circles again.
 */
const SearchAvatar = ({ photo, name, team }) => {
  const [broken, setBroken] = useState(false);
  const url = resolveImageUrl(photo, 120);
  const showImg = !!url && !broken;
  if (showImg) {
    return (
      <img
        src={url}
        alt=""
        className="w-9 h-9 rounded-full ring-1 ring-white/10 object-cover flex-shrink-0"
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full ring-1 ring-white/10 overflow-hidden flex-shrink-0">
      <AthleteAvatarFallback
        athlete={{ name, team, school_name: team }}
        className="w-full h-full"
      />
    </div>
  );
};

/* iter587 · StoryThumb — same treatment for story rows so blank
 * covers get a colored gradient + first-letter monogram instead of
 * an empty rectangle. Hash the title into a hue so each story gets
 * a stable, distinct color. */
const StoryThumb = ({ photo, title }) => {
  const [broken, setBroken] = useState(false);
  const url = resolveImageUrl(photo, 120);
  const showImg = !!url && !broken;
  if (showImg) {
    return (
      <img
        src={url}
        alt=""
        className="w-9 h-12 rounded-md object-cover flex-shrink-0"
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  const hue = Array.from((title || '?')).reduce((a, c) => a + c.charCodeAt(0), 0) * 37 % 360;
  const glyph = (title || '?')[0]?.toUpperCase() || '?';
  return (
    <div
      className="w-9 h-12 rounded-md flex-shrink-0 flex items-center justify-center ring-1 ring-white/10"
      style={{
        background: `linear-gradient(135deg, hsla(${hue}, 65%, 45%, 0.9) 0%, hsla(${(hue + 40) % 360}, 55%, 22%, 1) 100%)`,
      }}
      aria-hidden
    >
      <span className="text-white font-black text-sm drop-shadow">{glyph}</span>
    </div>
  );
};



const CFB_CATEGORIES = [
  { key: 'texas',        label: 'Texas Programs',     q: 'Texas' },
  { key: 'sec',          label: 'SEC',                q: 'SEC' },
  { key: 'big12',        label: 'Big 12',             q: 'Big 12' },
  { key: 'acc',          label: 'ACC',                q: 'ACC' },
  { key: 'big10',        label: 'Big Ten',            q: 'Big Ten' },
  { key: 'recruiting',   label: 'Recruiting',         q: 'Recruiting' },
  { key: 'transfer',     label: 'Transfer Portal',    q: 'Transfer Portal' },
  { key: 'draft',        label: 'NFL Draft',          q: 'NFL Draft' },
  { key: 'qb',           label: 'Quarterbacks',       q: 'Quarterback' },
  { key: 'skill',        label: 'Skill Players',      q: 'WR RB TE' },
  { key: 'defense',      label: 'Defensive Players',  q: 'Defense' },
];

const TRENDING_TERMS = [
  'Texas A&M', 'TCU Football', 'Big 12', 'SEC', 'Recruiting',
  'Transfer Portal', 'NFL Draft', 'Quarterbacks', 'NIL', 'Commitments',
];

/* ═══════════════════════════════════════════════════════════════════════
 * Small pieces
 * ═════════════════════════════════════════════════════════════════════ */

const SectionHeader = ({ kicker, title, action }) => (
  <div className="flex items-end justify-between mb-4 gap-3 px-5">
    <div>
      {kicker && <p className="font-italic text-[10px] uppercase tracking-[0.22em] text-blue-300 mb-1">{kicker}</p>}
      <h2 className="font-display text-xl sm:text-2xl uppercase leading-none text-white">{title}</h2>
    </div>
    {action}
  </div>
);

const HScroll = ({ children, testid }) => (
  <div
    className="flex gap-3 overflow-x-auto snap-x snap-mandatory pt-1.5 pb-3 px-5"
    style={{ scrollbarWidth: 'none' }}
    data-testid={testid}
  >
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * Recent Searches
 * ═════════════════════════════════════════════════════════════════════ */

const useRecentSearches = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setItems(JSON.parse(raw).slice(0, 5));
    } catch { /* ignore */ }
  }, []);
  const push = (term) => {
    if (!term?.trim()) return;
    setItems((prev) => {
      const next = [term, ...prev.filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, 5);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const remove = (term) => {
    setItems((prev) => {
      const next = prev.filter((t) => t !== term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const clear = () => {
    setItems([]);
    try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
  };
  return { items, push, remove, clear };
};

/* ═══════════════════════════════════════════════════════════════════════
 * Autocomplete (uses /api/search)
 * ═════════════════════════════════════════════════════════════════════ */

const Autocomplete = ({ query, onPick }) => {
  const [data, setData] = useState({ athletes: [], stories: [], teams: [], collections: [] });

  useEffect(() => {
    if (query.length < 2) { setData({ athletes: [], stories: [], teams: [], collections: [] }); return; }
    let cancel = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        if (cancel) return;
        setData({
          athletes:    (r.data.athletes || []).slice(0, 5),
          stories:     (r.data.stories || []).slice(0, 4),
          teams:       (r.data.teams || []).slice(0, 3),
          collections: (r.data.collections || []).slice(0, 3),
        });
      } catch { /* silent */ }
    };
    run();
    return () => { cancel = true; };
  }, [query]);

  if (query.length < 2) return null;
  const empty = !data.athletes.length && !data.stories.length && !data.teams.length && !data.collections.length;

  return (
    <div className="mx-5 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 overflow-hidden mb-4" data-testid="search-autocomplete">
      {empty && (
        <div className="px-4 py-8 text-center">
          <p className="text-white/70 text-sm">We couldn&apos;t find an exact match.</p>
          <p className="text-white/40 text-xs mt-1">Try a school name, athlete, or topic.</p>
          <button
            type="button"
            onClick={async () => {
              // iter563 · Fire an actual request to the backend so
              // the ask lands in the admin school-inquiries inbox
              // instead of silently doing nothing. Optimistic toast
              // regardless of network hiccups (the request is a
              // low-stakes lead — no need to block the user on retry).
              try {
                await axios.post(`${API}/profile-request`, { term: query });
                toast.success(`Request sent — we'll add "${query}" soon.`);
              } catch {
                toast.success(`Request sent — we'll review "${query}".`);
              }
              onPick({ type: 'request', term: query });
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ring-1 ring-blue-400/40 text-blue-200 text-[10px] uppercase tracking-widest font-bold hover:bg-blue-500/10"
            data-testid="search-request-profile"
          >
            Request &ldquo;{query}&rdquo; profile
          </button>
        </div>
      )}

      {data.athletes.length > 0 && (
        <div className="py-2">
          <p className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/40 font-bold">Athletes</p>
          {data.athletes.map((a) => (
            <Link
              key={a.slug}
              to={`/network/${a.slug}`}
              onClick={() => onPick({ type: 'athlete', term: a.name })}
              className="flex items-center gap-3 px-4 py-2 hover:bg-white/[0.04] transition"
              data-testid={`search-suggest-athlete-${a.slug}`}
              {...linkHoverProps(a.slug)}
            >
              <SearchAvatar
                photo={a.photo || a.network_hero_image}
                name={a.name}
                team={a.team || a.school_name}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm text-white truncate">{a.name}</p>
                  {(a.verified || a.claim_status === 'verified') && <BadgeCheck className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />}
                </div>
                <p className="text-[11px] text-white/40 truncate">
                  {[a.position, a.sport, a.team || a.school_name].filter(Boolean).join(' · ')}
                </p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {data.stories.length > 0 && (
        <div className="py-2 border-t border-white/[0.06]">
          <p className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/40 font-bold">Stories</p>
          {data.stories.map((s) => (
            <Link
              key={s.slug}
              to={`/stories/${s.slug}`}
              onClick={() => onPick({ type: 'story', term: s.name })}
              className="flex items-center gap-3 px-4 py-2 hover:bg-white/[0.04] transition"
              data-testid={`search-suggest-story-${s.slug}`}
            >
              <StoryThumb
                photo={s.thumbnail || s.cover_image}
                title={s.name}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{s.name}</p>
                <p className="text-[11px] text-white/40 truncate">{s.category || 'Story'}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
      {data.teams.length > 0 && (
        <div className="py-2 border-t border-white/[0.06]" data-testid="search-suggest-teams">
          <p className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/40 font-bold">Teams</p>
          {data.teams.map((t) => (
            <Link
              key={t.name}
              to={`/team/${encodeURIComponent(t.name)}`}
              onClick={() => onPick({ type: 'team', term: t.name })}
              className="flex items-center gap-3 px-4 py-2 hover:bg-white/[0.04] transition"
              data-testid={`search-suggest-team-${t.name}`}
            >
              <div className="w-9 h-9 rounded-full ring-1 ring-white/10 bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-black text-white/80 uppercase">{(t.name || '').split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{t.name}</p>
                <p className="text-[11px] text-white/40 truncate">{t.count || 0} athletes</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {data.collections.length > 0 && (
        <div className="py-2 border-t border-white/[0.06]" data-testid="search-suggest-collections">
          <p className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/40 font-bold">Collections</p>
          {data.collections.map((c) => (
            <Link
              key={c.slug || c.name}
              to={`/collections/${c.slug || ''}`}
              onClick={() => onPick({ type: 'collection', term: c.name })}
              className="flex items-center gap-3 px-4 py-2 hover:bg-white/[0.04] transition"
              data-testid={`search-suggest-collection-${c.slug || c.name}`}
            >
              <div className="w-9 h-9 rounded-lg ring-1 ring-white/10 bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-black text-white/80 uppercase">{(c.name || '')[0] || 'C'}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{c.name}</p>
                <p className="text-[11px] text-white/40 truncate">{c.description ? c.description.slice(0, 60) : 'Collection'}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
 * Athlete card + Program card + Story card (compact shared)
 * ═════════════════════════════════════════════════════════════════════ */

const AthleteCard = ({ a, size = 'md', testid }) => {
  const dims = size === 'sm' ? 'w-[150px]' : 'w-[190px]';
  const img = a.photo || a.network_hero_image;
  // iter229f · Empty photo = render school-color initials fill instead
  // of an empty black rectangle. Matches the identity-neutral treatment
  // Jay landed on across the athlete profile page.
  const schoolColor = a.school_primary_color
    || SCHOOL_PRIMARY_COLOR[a.school_slug]
    || '#3b82f6';
  return (
    <Link
      to={`/network/${a.slug}`}
      className={`snap-start flex-shrink-0 ${dims} group`}
      data-testid={testid}
      {...linkHoverProps(a.slug)}
    >
      <div
        className="relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-white/10"
        style={img ? { backgroundColor: '#0a0a0c' } : {
          background: `radial-gradient(circle at 30% 20%, ${schoolColor}66 0%, ${schoolColor}22 45%, #050505 100%)`,
          boxShadow: `inset 0 0 0 1px ${schoolColor}44`,
        }}
      >
        {img ? (
          <img
            src={resolveImageUrl(img, 400)}
            alt={a.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
            <AthleteAvatarFallback
              athlete={a}
              className="w-20 h-20"
              ringWidth={2}
              showInitials
              forceInitials
            />
            <p className="mt-3 text-[9px] uppercase tracking-[0.24em] font-black text-white/70">
              Claim Profile
            </p>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {(a.verified || a.claim_status === 'verified') && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-500/90 ring-1 ring-white/30 flex items-center justify-center">
            <BadgeCheck className="w-3.5 h-3.5 text-white" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-2.5">
          <p className="font-display text-sm text-white uppercase leading-tight line-clamp-1">{a.name}</p>
          <p className="text-[10px] text-white/60 mt-0.5 truncate">
            {[a.position, a.team || a.school_name].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>
    </Link>
  );
};

const StoryCard = ({ s, testid }) => (
  <Link
    to={`/stories/${s.slug}`}
    className="snap-start flex-shrink-0 w-[240px] group"
    data-testid={testid}
  >
    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
      <img
        src={resolveImageUrl(s.thumbnail || s.cover_image, 400)}
        alt={s.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      {s.breaking && (
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-black uppercase tracking-widest">Breaking</span>
      )}
      {!s.breaking && s.featured && (
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/15 ring-1 ring-white/25 text-white text-[9px] font-black uppercase tracking-widest">Featured</span>
      )}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); }}
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 ring-1 ring-white/15 text-white/80 flex items-center justify-center hover:bg-black/80"
        aria-label="Save"
      >
        <Bookmark className="w-3.5 h-3.5" />
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">{s.category || 'Story'}</p>
        <h3 className="font-display text-sm text-white uppercase leading-tight line-clamp-2 mt-0.5">{s.name}</h3>
        <p className="text-[10px] text-white/50 mt-1 truncate">{s.author || 'FLYT'} · {new Date(s.publish_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
      </div>
    </div>
  </Link>
);

/* ═══════════════════════════════════════════════════════════════════════
 * PAGE
 * ═════════════════════════════════════════════════════════════════════ */

const SearchPage = () => {
  const { user } = useUserAuth();
  const [urlParams, setUrlParams] = useSearchParams();
  const inputRef = useRef(null);
  // iter459j · Hydrate query state from `?q=` on mount so that:
  //   1. Tapping a trending chip / category / recent search →
  //      navigate('/search?q=X') actually POPULATES the input +
  //      fires the autocomplete inline results (previously landed
  //      users back on the empty discovery view — dead-end)
  //   2. Deep links + external shares (/search?q=arch+manning)
  //      arrive with the query already active
  //   3. Back/forward preserves the last search
  const [query, setQuery] = useState(() => urlParams.get('q') || '');
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const recent = useRecentSearches();

  // Rotating placeholder — cycles every 3.5s while empty and unfocused.
  useEffect(() => {
    if (query) return undefined;
    const t = setInterval(() => setPlaceholderIdx((i) => (i + 1) % PLACEHOLDERS.length), 3500);
    return () => clearInterval(t);
  }, [query]);

  const [athletes, setAthletes] = useState([]);
  const [stories, setStories] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [pulse, setPulse] = useState([]);

  useEffect(() => {
    // Kick off all discovery fetches in parallel. Fails silently per-row.
    // iter816 · Jay: dropped the MLB and school-programs (Explore
    // Programs) fetches — both sections were removed below since
    // neither has enough real content populated yet.
    Promise.allSettled([
      axios.get(`${API}/athletes?limit=24&slim=true&sport=football`),
      axios.get(`${API}/stories?channel=dreams_league&limit=12&since_days=14`),
      axios.get(`${API}/stories?coming_soon=true&channel=dreams_league&limit=6`),
      axios.get(`${API}/pulse/live?limit=8`),
    ]).then((results) => {
      const [a, s, cs, pl] = results;
      if (a.status === 'fulfilled') setAthletes(a.value.data?.athletes || a.value.data || []);
      if (s.status === 'fulfilled') setStories(s.value.data || []);
      if (cs.status === 'fulfilled') setComingSoon(cs.value.data || []);
      if (pl.status === 'fulfilled') setPulse(pl.value.data?.events || []);
    });
  }, []);

  const submit = useCallback((term) => {
    const q = (term ?? query).trim();
    if (!q) return;
    recent.push(q);
    // iter459j · Set the query state in-place AND sync the URL so:
    //  - trending chips + categories + recent chips populate the
    //    input + fire the autocomplete inline (no dead-end reload)
    //  - the URL is shareable (?q=arch+manning)
    //  - back/forward works
    setQuery(q);
    setUrlParams({ q }, { replace: false });
    // Auto-scroll the results into view + focus the input so the
    // keyboard drops away and the results are the first thing they
    // see. Small delay lets React commit the state before the DOM
    // measurement.
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        inputRef.current?.blur();
      } catch (_) { /* noop */ }
    }, 30);
  }, [query, recent, setUrlParams]);

  // iter459j · Keep the URL param in lockstep with the input while
  // the user is typing — debounced so we don't spam history entries.
  useEffect(() => {
    const q = query.trim();
    const current = urlParams.get('q') || '';
    if (q === current) return undefined;
    const t = setTimeout(() => {
      if (q) setUrlParams({ q }, { replace: true });
      else setUrlParams({}, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Recently Active — pulse events for athlete actors only.
  const recentlyActive = useMemo(
    () => pulse.filter((e) => e.actor_type === 'athlete' && e.actor_slug).slice(0, 8),
    [pulse]
  );
  // What's Happening Now — top 5 meaningful events.
  const happeningNow = useMemo(() => pulse.slice(0, 5), [pulse]);

  // Filter athletes for CFB discovery (already filtered by sport param but
  // guard against upstream drift).
  const cfbAthletes = useMemo(
    () => athletes.filter((a) => !a.sport || /football/i.test(a.sport)).slice(0, 12),
    [athletes]
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24" data-testid="search-page">
      {/* Compact header + input */}
      <header className="pt-6 sm:pt-8 pb-5 px-5">
        <p className="text-[10px] uppercase tracking-[0.28em] text-blue-300 font-bold mb-1">Discovery</p>
        <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white">Search Flyt</h1>
        <p className="text-white/60 text-sm mt-1">Find athletes, programs, teams, stories, and collections.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="mt-4 relative"
        >
          {/* iter474 · Icon lives inside the input's left padding —
              global `input[type="text"] { padding:14px 18px }` in
              App.css was outranking Tailwind's `pl-11`, so we pin
              the horizontal padding inline so the icon never
              overlaps typed text or the placeholder on native iOS. */}
          <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            autoCorrect="off"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={PLACEHOLDERS[placeholderIdx]}
            className="w-full py-3.5 rounded-2xl bg-neutral-900/80 ring-1 ring-white/10 focus:ring-blue-400/50 text-white text-[15px] outline-none placeholder-white/30 appearance-none"
            style={{
              WebkitAppearance: 'none',
              backgroundColor: 'rgba(255,255,255,0.05)',
              paddingLeft: 44,
              paddingRight: 44,
            }}
            data-testid="search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60"
              aria-label="Clear"
              data-testid="search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
      </header>

      {/* Autocomplete inline */}
      <Autocomplete
        query={query}
        onPick={(pick) => {
          if (pick.type === 'request') submit(pick.term);
          else recent.push(pick.term);
        }}
      />

      {/* Recent searches */}
      {query.length < 2 && recent.items.length > 0 && (
        <section className="pb-2" data-testid="search-recent">
          <div className="flex items-center justify-between mb-2 px-5">
            <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Recent</p>
            <button type="button" onClick={recent.clear} className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white" data-testid="search-recent-clear">Clear all</button>
          </div>
          <div className="flex gap-2 flex-wrap px-5">
            {recent.items.map((t) => (
              <div key={t} className="inline-flex items-center gap-1 pl-3 pr-1 py-1.5 rounded-full ring-1 ring-white/15 bg-white/[0.04]">
                <button type="button" onClick={() => submit(t)} className="text-[12px] text-white" data-testid={`search-recent-${t}`}>{t}</button>
                <button type="button" onClick={() => recent.remove(t)} className="w-5 h-5 rounded-full hover:bg-white/10 flex items-center justify-center text-white/40" aria-label={`Remove ${t}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Only show discovery rows when NOT actively typing */}
      {query.length < 2 && (
        <>
          {/* Explore College Football */}
          <section className="pt-6" data-testid="search-explore-cfb">
            <SectionHeader kicker="The main event" title="Explore College Football" />
            <div className="flex gap-2 overflow-x-auto no-scrollbar pt-1.5 pb-3 px-5">
              {CFB_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => submit(c.q)}
                  className="flex-shrink-0 px-4 py-2 rounded-full ring-1 ring-white/15 bg-white/[0.04] text-white text-[12px] font-bold uppercase tracking-widest hover:bg-white/[0.1] transition"
                  data-testid={`search-cfb-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* Trending Searches */}
          <section className="pt-6" data-testid="search-trending">
            <SectionHeader kicker="What everyone's asking" title="Trending Searches" />
            <div className="flex gap-2 overflow-x-auto no-scrollbar pt-1.5 pb-3 px-5">
              {TRENDING_TERMS.map((t, i) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => submit(t)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full ring-1 ring-blue-400/25 bg-blue-500/[0.06] text-blue-100 text-[12px] font-bold uppercase tracking-widest hover:bg-blue-500/[0.14] transition"
                  data-testid={`search-trending-${i}`}
                >
                  <Flame className="w-3 h-3" /> {t}
                </button>
              ))}
            </div>
          </section>

          {/* What's Happening Now — Pulse recap */}
          {happeningNow.length > 0 && (
            <section className="pt-6" data-testid="search-happening">
              <SectionHeader
                kicker="Live from the network"
                title="What's Happening Now"
                action={
                  <Link to="/pulse" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-blue-300 hover:text-blue-200">
                    View the Pulse <ArrowRight className="w-3 h-3" />
                  </Link>
                }
              />
              <div className="px-5 space-y-2">
                {happeningNow.map((ev) => (
                  <Link
                    key={ev.id}
                    to={eventHref(ev)}
                    className="flex items-start gap-3 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-3 hover:bg-white/[0.06] transition"
                    data-testid={`search-happening-${ev.id}`}
                  >
                    <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-white leading-snug">{ev.headline}</p>
                      <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold mt-0.5">
                        {(ev.event_type || '').replace(/_/g, ' ')} · {timeAgo(ev.created_at)}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30 mt-1 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Recently Active */}
          {recentlyActive.length > 0 && (
            <section className="pt-6" data-testid="search-recently-active">
              <SectionHeader kicker="Fresh moves" title="Recently Active" />
              <HScroll testid="search-recently-active-scroll">
                {recentlyActive.map((ev) => (
                  <Link
                    key={ev.id}
                    to={`/network/${ev.actor_slug}`}
                    className="snap-start flex-shrink-0 w-[170px] group"
                    data-testid={`search-recently-active-${ev.id}`}
                  >
                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                      <img
                        src={resolveImageUrl(ev.image_url, 400)}
                        alt={ev.actor_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      {ev.verified && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-500/90 ring-1 ring-white/30 flex items-center justify-center">
                          <BadgeCheck className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-2.5">
                        <p className="font-display text-sm text-white uppercase leading-tight line-clamp-1">{ev.actor_name}</p>
                        <p className="text-[10px] text-emerald-300 mt-0.5 truncate uppercase tracking-widest font-bold">{(ev.event_type || '').replace(/_/g, ' ')}</p>
                        <p className="text-[10px] text-white/50 mt-0.5">{timeAgo(ev.created_at)} ago</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </HScroll>
            </section>
          )}

          {/* Fresh CFB Stories */}
          {stories.length > 0 && (
            <section className="pt-6" data-testid="search-fresh-stories">
              <SectionHeader
                kicker="Recently published"
                title="Fresh College Football Stories"
                action={
                  <Link to="/stories" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-blue-300 hover:text-blue-200">
                    All stories <ArrowRight className="w-3 h-3" />
                  </Link>
                }
              />
              <HScroll testid="search-fresh-stories-scroll">
                {stories.slice(0, 12).map((s) => (
                  <StoryCard key={s.id} s={s} testid={`search-story-${s.slug}`} />
                ))}
              </HScroll>
            </section>
          )}

          {/* Discover CFB Athletes */}
          {cfbAthletes.length > 0 && (
            <section className="pt-6" data-testid="search-discover-athletes">
              <SectionHeader kicker="Names to know" title="Discover College Football Athletes" />
              <HScroll testid="search-discover-athletes-scroll">
                {cfbAthletes.map((a) => (
                  <AthleteCard key={a.slug} a={a} testid={`search-athlete-${a.slug}`} />
                ))}
              </HScroll>
            </section>
          )}

          {/* iter816 · Jay: removed Explore Programs and Explore MLB —
              neither has enough real content populated yet to earn a
              spot on Search. */}

          {/* Coming Soon */}
          {comingSoon.length > 0 && (
            <section className="pt-6 pb-10" data-testid="search-coming-soon">
              <SectionHeader kicker="On the calendar" title="Coming Soon" />
              <HScroll testid="search-coming-soon-scroll">
                {comingSoon.map((s) => {
                  const releaseAt = s.premiere_at || s.publish_date;
                  const isLive = releaseAt && new Date(releaseAt).getTime() <= Date.now();
                  return (
                    <Link
                      key={s.id}
                      to={`/stories/${s.slug}`}
                      className="snap-start flex-shrink-0 w-[240px] group"
                      data-testid={`search-coming-soon-${s.slug}`}
                    >
                      <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                        <img src={resolveImageUrl(s.thumbnail || s.cover_image, 400)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-blue-500/90 text-white text-[9px] font-black uppercase tracking-widest">
                          {isLive ? 'Available' : 'Premiere'}
                        </span>
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-[10px] uppercase tracking-widest text-white/60 mb-0.5">{s.athlete_name || s.author}</p>
                          <h3 className="font-display text-sm text-white uppercase leading-tight line-clamp-2">{s.name}</h3>
                          <p className="text-[10px] text-white/60 mt-1">{releaseAt ? new Date(releaseAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</p>
                          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-[10px] font-black uppercase tracking-widest">
                            {isLive
                              ? (<><Play className="w-3 h-3" /> Watch</>)
                              : (<><Bell className="w-3 h-3" /> Notify me</>)}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </HScroll>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;
